import { useEffect, useState } from 'react';
import { matchMutation, useIsMutating, useQueryClient } from '@tanstack/react-query';
import { processDraftKeys } from '@/api/query-keys';

// A save cycle runs from the first pending draft write until none is pending;
// one failed write in it means its rolled-back edit must not end in "Saved".
export function useDraftSaveStatus(processId: string) {
  const queryClient = useQueryClient();
  const pendingCount = useIsMutating({ mutationKey: processDraftKeys.mutations(processId) });
  const [saveFailed, setSaveFailed] = useState(false);

  useEffect(
    function trackFailedWritesPerCycle() {
      const mutationKey = processDraftKeys.mutations(processId);
      const cache = queryClient.getMutationCache();
      return cache.subscribe((event) => {
        if (event.type !== 'updated' || !matchMutation({ mutationKey }, event.mutation)) return;
        if (event.action.type === 'error') setSaveFailed(true);
        // A mutation whose onMutate returns a context dispatches `pending` a second
        // time, with that context; only the first dispatch may start a cycle.
        if (
          event.action.type === 'pending' &&
          event.action.context === undefined &&
          cache.findAll({ mutationKey, status: 'pending' }).length === 1
        ) {
          setSaveFailed(false);
        }
      });
    },
    [queryClient, processId]
  );

  return { isSaving: pendingCount > 0, saveFailed };
}
